import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import './ProjectDashboard.css';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface ProjectDashboardProps {
  folderId: string;
  folderName: string;
  folderDescription?: string;
}

interface ProjectStatistics {
  total_test_cases: number;
  total_test_scenarios: number;
  total_test_runs: number;
  active_test_runs: number;
  completed_test_runs: number;
  pass_rate: number;
  test_case_folders: number;
  test_scenario_folders: number;
  test_results: {
    passed: number;
    failed: number;
    blocked: number;
    not_run: number;
    total: number;
  };
}

interface Activity {
  type: string;
  description: string;
  user: string;
  timestamp: string;
  entity_id: number;
  entity_type: string;
}

interface TrendData {
  date: string;
  passed: number;
  failed: number;
  blocked: number;
  total: number;
}

export const ProjectDashboard: React.FC<ProjectDashboardProps> = ({ folderId, folderName, folderDescription }) => {
  const navigate = useNavigate();
  const [statistics, setStatistics] = useState<ProjectStatistics | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [trendData, setTrendData] = useState<TrendData[]>([]);
  const [trendDays, setTrendDays] = useState<number>(30);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      console.log('Fetching dashboard data for folder:', folderId);
      
      // Fetch folder-specific statistics
      const [statsResponse, activityResponse, trendResponse] = await Promise.all([
        api.get(`/api/v1/project/statistics?folder_id=${folderId}`),
        api.get(`/api/v1/project/activity?folder_id=${folderId}&limit=5`),
        api.get(`/api/v1/project/execution-trend?folder_id=${folderId}&days=${trendDays}`)
      ]);

      console.log('Dashboard data fetched successfully:', {
        stats: statsResponse.data,
        activities: activityResponse.data,
        trend: trendResponse.data
      });

      setStatistics(statsResponse.data);
      setActivities(activityResponse.data);
      setTrendData(trendResponse.data);
    } catch (error: any) {
      console.error('Error fetching dashboard data:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchDashboardData();
  }, [folderId, trendDays]);

  const getPassRateBadge = (passRate: number) => {
    if (passRate >= 90) return { text: 'Excellent', className: 'badge-success' };
    if (passRate >= 75) return { text: 'Good', className: 'badge-warning' };
    return { text: 'Needs Attention', className: 'badge-danger' };
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'test_run_completed':
        return '▶️';
      case 'test_run_started':
        return '▶️';
      case 'import':
        return '📥';
      case 'update':
        return '✏️';
      default:
        return '📋';
    }
  };

  const getTimeAgo = (timestamp: string) => {
    const now = new Date();
    const past = new Date(timestamp);
    const diffMs = now.getTime() - past.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
    if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
  };

  // Prepare chart data
  const lineChartData = {
    labels: trendData.map(d => {
      const date = new Date(d.date);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }),
    datasets: [
      {
        label: 'Passed',
        data: trendData.map(d => d.passed),
        borderColor: '#28a745',
        backgroundColor: 'rgba(40, 167, 69, 0.1)',
        tension: 0.4,
        fill: true
      },
      {
        label: 'Failed',
        data: trendData.map(d => d.failed),
        borderColor: '#dc3545',
        backgroundColor: 'rgba(220, 53, 69, 0.1)',
        tension: 0.4,
        fill: true
      }
    ]
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 5
        }
      }
    }
  };

  const doughnutChartData = statistics ? {
    labels: ['Passed', 'Failed', 'Blocked', 'Not Run'],
    datasets: [{
      data: [
        statistics.test_results.passed,
        statistics.test_results.failed,
        statistics.test_results.blocked,
        statistics.test_results.not_run
      ],
      backgroundColor: [
        '#28a745',
        '#dc3545',
        '#ffc107',
        '#6c757d'
      ],
      borderWidth: 0
    }]
  } : null;

  const doughnutChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
      }
    }
  };

  if (loading) {
    return (
      <div className="project-dashboard">
        <div className="loading">Loading dashboard...</div>
      </div>
    );
  }

  if (!statistics) {
    return (
      <div className="project-dashboard">
        <div className="error">Failed to load dashboard data</div>
      </div>
    );
  }

  const passRateBadge = getPassRateBadge(statistics.pass_rate);

  return (
    <div className="project-dashboard">
      {/* Project Header */}
      <div className="dashboard-header">
        <h1>🚀 {folderName}</h1>
        {folderDescription && (
          <div className="header-description">{folderDescription}</div>
        )}
        <div className="header-meta">
          <span>📅 Last Updated: <strong>{new Date().toLocaleDateString()}</strong></span>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="stats-grid">
        <div className="stat-card" onClick={() => navigate('/test-cases')}>
          <div className="stat-icon blue">📋</div>
          <div className="stat-content">
            <h3>Test Cases</h3>
            <div className="value">{statistics.total_test_cases}</div>
            <div className="subtext">Across {statistics.test_case_folders} folders</div>
          </div>
        </div>

        <div className="stat-card" onClick={() => navigate('/scenarios')}>
          <div className="stat-icon green">📝</div>
          <div className="stat-content">
            <h3>Test Scenarios</h3>
            <div className="value">{statistics.total_test_scenarios}</div>
            <div className="subtext">Across {statistics.test_scenario_folders} folders</div>
          </div>
        </div>

        <div className="stat-card" onClick={() => navigate('/test-runs')}>
          <div className="stat-icon purple">▶️</div>
          <div className="stat-content">
            <h3>Test Runs</h3>
            <div className="value">{statistics.total_test_runs}</div>
            <div className="subtext">
              {statistics.active_test_runs} active, {statistics.completed_test_runs} completed
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon orange">✅</div>
          <div className="stat-content">
            <h3>Pass Rate</h3>
            <div className="value">{statistics.pass_rate}%</div>
            <div className="subtext">
              <span className={`badge ${passRateBadge.className}`}>{passRateBadge.text}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="two-column">
        {/* Execution Trend Chart */}
        <div className="card">
          <div className="card-header">
            <h2>📊 Test Execution Trend</h2>
            <div className="filter">
              <button
                className={`filter-btn ${trendDays === 7 ? 'active' : ''}`}
                onClick={() => setTrendDays(7)}
              >
                7 Days
              </button>
              <button
                className={`filter-btn ${trendDays === 30 ? 'active' : ''}`}
                onClick={() => setTrendDays(30)}
              >
                30 Days
              </button>
              <button
                className={`filter-btn ${trendDays === 90 ? 'active' : ''}`}
                onClick={() => setTrendDays(90)}
              >
                90 Days
              </button>
            </div>
          </div>
          <div className="chart-container">
            <Line data={lineChartData} options={lineChartOptions} />
          </div>
        </div>

        {/* Test Execution Summary */}
        <div className="card">
          <div className="card-header">
            <h2>📈 Execution Summary</h2>
          </div>
          <div className="execution-summary">
            <div className="execution-item">
              <div className="icon passed">✓</div>
              <div className="details">
                <div className="label">
                  <span>Passed</span>
                  <span className="count">
                    {statistics.test_results.passed} (
                    {statistics.test_results.total > 0
                      ? ((statistics.test_results.passed / statistics.test_results.total) * 100).toFixed(1)
                      : 0}%)
                  </span>
                </div>
                <div className="progress-bar">
                  <div
                    className="progress-fill passed"
                    style={{
                      width: statistics.test_results.total > 0
                        ? `${(statistics.test_results.passed / statistics.test_results.total) * 100}%`
                        : '0%'
                    }}
                  ></div>
                </div>
              </div>
            </div>

            <div className="execution-item">
              <div className="icon failed">✗</div>
              <div className="details">
                <div className="label">
                  <span>Failed</span>
                  <span className="count">
                    {statistics.test_results.failed} (
                    {statistics.test_results.total > 0
                      ? ((statistics.test_results.failed / statistics.test_results.total) * 100).toFixed(1)
                      : 0}%)
                  </span>
                </div>
                <div className="progress-bar">
                  <div
                    className="progress-fill failed"
                    style={{
                      width: statistics.test_results.total > 0
                        ? `${(statistics.test_results.failed / statistics.test_results.total) * 100}%`
                        : '0%'
                    }}
                  ></div>
                </div>
              </div>
            </div>

            <div className="execution-item">
              <div className="icon blocked">⏸</div>
              <div className="details">
                <div className="label">
                  <span>Blocked</span>
                  <span className="count">
                    {statistics.test_results.blocked} (
                    {statistics.test_results.total > 0
                      ? ((statistics.test_results.blocked / statistics.test_results.total) * 100).toFixed(1)
                      : 0}%)
                  </span>
                </div>
                <div className="progress-bar">
                  <div
                    className="progress-fill blocked"
                    style={{
                      width: statistics.test_results.total > 0
                        ? `${(statistics.test_results.blocked / statistics.test_results.total) * 100}%`
                        : '0%'
                    }}
                  ></div>
                </div>
              </div>
            </div>

            <div className="execution-item">
              <div className="icon not-run">○</div>
              <div className="details">
                <div className="label">
                  <span>Not Run</span>
                  <span className="count">
                    {statistics.test_results.not_run} (
                    {statistics.test_results.total > 0
                      ? ((statistics.test_results.not_run / statistics.test_results.total) * 100).toFixed(1)
                      : 0}%)
                  </span>
                </div>
                <div className="progress-bar">
                  <div
                    className="progress-fill not-run"
                    style={{
                      width: statistics.test_results.total > 0
                        ? `${(statistics.test_results.not_run / statistics.test_results.total) * 100}%`
                        : '0%'
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Pie Chart */}
          {doughnutChartData && (
            <div className="chart-container-small">
              <Doughnut data={doughnutChartData} options={doughnutChartOptions} />
            </div>
          )}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="card">
        <div className="card-header">
          <h2>🕐 Recent Activity</h2>
        </div>
        <div className="activity-feed">
          {activities.length > 0 ? (
            activities.map((activity, index) => (
              <div key={index} className="activity-item">
                <div className={`activity-icon ${activity.type}`}>
                  {getActivityIcon(activity.type)}
                </div>
                <div className="activity-content">
                  <div className="text">
                    <strong>{activity.description}</strong>
                  </div>
                  <div className="time">
                    {getTimeAgo(activity.timestamp)} • by {activity.user}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="no-activity">No recent activity</div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card">
        <div className="card-header">
          <h2>⚡ Quick Actions</h2>
        </div>
        <div className="quick-actions">
          <button className="action-btn primary" onClick={() => navigate('/test-cases')}>
            <span>📋</span>
            <span>Go to Test Cases</span>
          </button>
          <button className="action-btn primary" onClick={() => navigate('/scenarios')}>
            <span>📝</span>
            <span>Go to Test Scenarios</span>
          </button>
          <button className="action-btn secondary" onClick={() => navigate('/test-runs/create')}>
            <span>▶️</span>
            <span>Create Test Run</span>
          </button>
          <button className="action-btn secondary" onClick={() => navigate('/test-cases')}>
            <span>📥</span>
            <span>Import Data</span>
          </button>
          <button className="action-btn secondary" onClick={() => navigate('/test-runs')}>
            <span>📊</span>
            <span>View Test Runs</span>
          </button>
        </div>
      </div>
    </div>
  );
};
